/**
 * Cart recommendations ("You may also like") - add / increase / decrease / remove.
 *
 * snippets/cart-recommendations-fallback.liquid renders each card's Add to
 * Cart button using snippets/quick-add-quantity-atc - the same quantity
 * stepper + remove-button markup snippets/product-item.liquid uses elsewhere
 * in the theme.
 *
 * A product already in the cart is excluded server-side from this list (see
 * that snippet's `item_in_cart > 0` skip) - it's already visible with its
 * own quantity controls in the cart's line-item list above, so showing it
 * again here too would be redundant. That means the stepper (+/-/remove)
 * rendered by quick-add-quantity-atc realistically only shows for the brief
 * instant between an Add to Cart click and the resulting server refresh
 * (below) removing the card - it's kept wired up (rather than stripped
 * down to a one-shot button) so behavior stays correct if that timing ever
 * changes, and so multi-variant "Choose options" products still get the
 * same modal-opening behavior as the theme's own product grids.
 *
 * That markup's native interactivity (ProductItem() in theme.js, via
 * initQuickAddTemplateCounters / initQuickAddQuantitySelectors) only ever
 * binds inside a registered product-item grid section - this compact card
 * isn't part of one, so none of it runs here without this file (the exact
 * same gap assets/empty-search.js's popular-products carousel had, and
 * fixes the same way).
 *
 * "Keep the global add-to-cart functionality" here means: use the same
 * /cart/add.js and /cart/change.js endpoints, and emit the same
 * "cart:updating" / "cart:updated" / "quick-cart:updated" events (via
 * window.emit, the theme's own event bus - see assets/theme.js, where
 * `window.emit = <bus>.emit` and `window.on = <bus>.on`) that every other
 * add-to-cart flow in the theme already uses - so the cart drawer's item
 * count, subtotal, and line list all stay in sync exactly as they would
 * from any other Add to Cart button. Both the drawer and the cart page
 * subscribe to those same events to re-fetch and replace this section's
 * markup from the server (refreshQuickCart() / refreshCart() in theme.js),
 * which is what actually removes the card from view once the liquid
 * template no longer includes it.
 */
(function () {
  "use strict";

  var WRAPPER_SELECTOR = "[data-cross-sells] .product-item__quick-add-wrapper";

  function syncWrapperQuantity(wrapper, quantity) {
    wrapper.dataset.quantityInCart = String(quantity);
    var input = wrapper.querySelector("[data-product-quantity-input]");
    if (input) input.value = quantity;
    if (quantity > 0) {
      wrapper.classList.add("active");
    } else {
      wrapper.classList.remove("active");
    }
  }

  function addToCart(variantId, wrapper) {
    // Disable immediately so a second click during the round trip below
    // can't fire a duplicate add before the card is removed from the list.
    var addBtn = wrapper.querySelector(".product-item__quick-add");
    if (addBtn) addBtn.disabled = true;

    if (typeof window.emit === "function") window.emit("cart:updating");
    fetch("/cart/add.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function () {
        return fetch("/cart.js", { credentials: "include" }).then(function (res) {
          return res.json();
        });
      })
      .then(function (cart) {
        // No local stepper/active-state update here: the product is now
        // excluded server-side (item_in_cart > 0), so the "cart:updated"
        // emit below triggers a server refresh that removes this card
        // entirely rather than needing it to show a stepper first.
        if (typeof window.emit !== "function") return;
        window.emit("quick-cart:updated");
        window.emit("cart:updated", { cart: cart });
      })
      .catch(function () {
        if (addBtn) addBtn.disabled = false;
        /* fire-and-forget, matches the theme's own add flow */
      });
  }

  function changeCartLine(variantId, newQuantity, wrapper) {
    newQuantity = Math.max(0, newQuantity);
    if (typeof window.emit === "function") window.emit("cart:updating");
    fetch("/cart/change.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: newQuantity }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        syncWrapperQuantity(wrapper, newQuantity);
        if (typeof window.emit !== "function") return;
        window.emit("quick-cart:updated");
        window.emit("cart:updated", { cart: cart });
      })
      .catch(function () {
        /* fire-and-forget, matches changeCartLine in assets/empty-search.js */
      });
  }

  // Syncs each card's "Add to Cart" vs. quantity-stepper display with its own
  // server-rendered data-quantity-in-cart value. Needed on init (SSR never
  // stamps the `.active` class that flips the CSS toggle, only the numeric
  // attribute - see theme.css's `.product-item__quick-add-wrapper.active`
  // rules) AND again whenever the cart drawer/cart page re-renders this
  // section's markup from the server (cart.js's refreshQuickCart() replaces
  // the whole cross-sells container's innerHTML on every cart mutation,
  // which would otherwise silently reset every card back to its un-clicked
  // "Add to Cart" state even for items still in the cart).
  // Multi-variant products ("Choose options") are excluded: item_in_cart
  // there is a sum across every variant of the product, but the stepper can
  // only ever adjust the single variant on the button
  // (prod.variants.first.id) - showing it would let +/- silently act on a
  // variant the customer may not have added, so those wrappers always stay
  // on "Choose options" instead, same as the theme's own product grids.
  function syncActiveStates(root) {
    var wrappers = (root || document).querySelectorAll(WRAPPER_SELECTOR);
    for (var i = 0; i < wrappers.length; i++) {
      if (wrappers[i].dataset.hasVariants === "true") continue;
      var qty = parseInt(wrappers[i].dataset.quantityInCart, 10) || 0;
      if (qty > 0) wrappers[i].classList.add("active");
    }
  }

  // "Choose options" opens the theme's own quick-add modal (assets/theme.js's
  // quickProductModal), which - for reasons internal to that vendor component
  // that aren't safe/appropriate to patch here (theme.js is a pre-compiled
  // vendor file this project doesn't rebuild - see CLAUDE.md) - was observed
  // to leave the cart drawer closed after adding a variant from within that
  // modal while it was open on top of the drawer, instead of leaving the
  // drawer open with the newly-added item visible.
  //
  // Two earlier attempts at compensating for this (a single re-open right on
  // "cart:updated", then several delayed re-opens guessing at timing) didn't
  // hold up under real testing - whatever closes the drawer wasn't reliably
  // beaten by re-opening at a guessed moment. Instead of guessing a timing
  // window, this watches the drawer's own element directly
  // (.quick-cart__wrapper's "active" class, the theme's own open/closed
  // flag - see classes$8.active / openQuickCart()/close() in theme.js) for a
  // few seconds after the add completes, and immediately puts "active" back
  // the instant anything removes it. This self-corrects regardless of what
  // removes the class or when, without needing to know why.
  function keepDrawerOpenBriefly() {
    var wrapper = document.querySelector(".quick-cart__wrapper");
    if (!wrapper) return;

    wrapper.classList.add("active");
    if (typeof window.emit === "function") window.emit("quick-cart:open");

    var stopAt = Date.now() + 3000;
    var observer = new MutationObserver(function () {
      if (!wrapper.classList.contains("active")) {
        wrapper.classList.add("active");
        if (typeof window.emit === "function") window.emit("quick-cart:open");
      }
      if (Date.now() >= stopAt) observer.disconnect();
    });
    observer.observe(wrapper, { attributes: true, attributeFilter: ["class"] });
    setTimeout(function () {
      observer.disconnect();
    }, 3000);
  }

  function reopenDrawerAfterQuickAdd(wrapper) {
    if (typeof window.on !== "function") return;
    if (!wrapper.closest(".cart-recommendations--drawer")) return;

    var unsubAdded, unsubClosed;
    unsubAdded = window.on("cart:updated", function () {
      unsubAdded();
      unsubClosed();
      keepDrawerOpenBriefly();
    });
    // Clean up if the modal is dismissed without adding anything, so this
    // doesn't keep listening indefinitely for a "cart:updated" that never
    // comes from this particular quick-add.
    unsubClosed = window.on(["quick-add:close", "quick-view:close"], function () {
      unsubAdded();
      unsubClosed();
    });
  }

  function handleClick(e) {
    var wrapper = e.target.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    var addBtn = wrapper.querySelector(".product-item__quick-add");
    var variantId = addBtn && addBtn.dataset.variantId;
    if (!variantId) return;

    var stepperBtn = e.target.closest(
      "[data-add-quantity], [data-subtract-quantity], [data-remove-item]"
    );
    if (stepperBtn) {
      var currentQty = parseInt(wrapper.dataset.quantityInCart, 10) || 0;
      var nextQty = currentQty;
      if (stepperBtn.hasAttribute("data-add-quantity")) {
        nextQty = currentQty + 1;
      } else if (stepperBtn.hasAttribute("data-subtract-quantity")) {
        nextQty = currentQty - 1;
      } else if (stepperBtn.hasAttribute("data-remove-item")) {
        nextQty = 0;
      }
      changeCartLine(variantId, nextQty, wrapper);
      return;
    }

    if (!e.target.closest(".product-item__quick-add")) return;

    if (wrapper.dataset.hasVariants === "true") {
      if (typeof window.emit !== "function") return;
      var card = wrapper.closest(".cart-rec-card__add");
      window.emit("quick-add:open", null, {
        type: "quick-add",
        productUrl: card && card.dataset.productUrl,
        productTitle: card && card.dataset.title,
      });
      reopenDrawerAfterQuickAdd(wrapper);
      return;
    }

    var quantityInCart = parseInt(wrapper.dataset.quantityInCart, 10) || 0;
    if (quantityInCart < 1) {
      addToCart(variantId, wrapper);
    } else {
      wrapper.classList.add("active");
    }
  }

  function init() {
    document.addEventListener("click", handleClick);
    syncActiveStates();

    // Re-sync after any cart mutation replaces the cross-sells container's
    // innerHTML (see refreshQuickCart()/_initCrossSells() in theme.js) -
    // coalesced onto a single animation frame since several mutation records
    // can land for one re-render.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        syncActiveStates();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }
  }
})();
